import React, { useEffect, useRef, useState } from "react";
import "./Dashboard.css";
import "../../DailyRegister.css";
import DetailsTab from "../Details/DetailsTab";
import { calculateMilkValues } from "../../../Utils/Calculations";
import { onlyNumber } from "../../../Utils/commonFuntion";
import Button from '@mui/material/Button';
import PrintIcon from '@mui/icons-material/Print';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EditIcon from '@mui/icons-material/Edit';
import http from "../../../Utils/axiosConfig";
import { apiMessage } from "../../../Utils/apiMessage";
import ToastService from "../../../Utils/ToastService";

// Explicit IANA timezone rather than manual UTC-offset math — the latter is
// what caused the earlier frozen-clock/wrong-shift bug this replaces.
const getTodayISO = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }); // en-CA -> YYYY-MM-DD
const getCurrentShift = () => {
  const hour = Number(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata", hour: "2-digit", hourCycle: "h23" }));
  return hour >= 1 && hour < 15 ? "morning" : "evening";
};
// Same calendar day either way — plain string reshuffle, no Date object
// (and therefore no timezone) involved, unlike parsing "YYYY-MM-DD" back
// into a Date just to reformat it.
const formatDisplayDate = (iso) => (iso ? iso.split("-").reverse().join("/") : "");

// dairy_id is an INT from the API; the क्रमांक input gives a string. Both lookups have
// to convert, and having two of them is what broke: the typing path converted, the
// date/shift effect did not, so changing the date silently resolved the member to null
// and the button read "सभासद अस्तित्वात नाही!".
const findMemberByDairyId = (list, value) =>
  list.find((m) => m.dairy_id === Number(value)) || null;

const DashBoard = () => {
  const [membersData, setMembersData] = useState([]);
  const [todaysData, setTodaysData] = useState([]);
  const [showDetailsTab, setShowDetailsTab] = useState(false);
  const [showTodaysData, setShowTodaysData] = useState(null);
  const [memberInput, setMemberInput] = useState(1);
  const memberInputRef = useRef(memberInput);
  const [selectedMember, setSelectedMember] = useState(null);
  const [entryDate, setEntryDate] = useState(getTodayISO);
  const [entryShift, setEntryShift] = useState(getCurrentShift);
  const [backdatedMode, setBackdatedMode] = useState(false);
  const [Stats, setStats] = useState({ buffaloMilk: "0", buffaloRate: "0", cowMilk: "0", cowRate: "0", completedMembers: 0, totalMembers: 0 });
  const [rateError, setRateError] = useState("");
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [customChart, setCustomChart] = useState([]);
  const [formData, setFormData] = useState({
    qty: "",
    fat: "",
    snf: "",
    rate: "0",
    total_rate: "0",
    milkType: "",
  });

  // Keeps entryDate/entryShift synced to "now" — but only while the admin
  // hasn't opened the backdated-entry panel, so a manually picked date/shift
  // never gets silently snapped back to today mid-entry.
  useEffect(() => {
    if (backdatedMode) return;
    setEntryDate(getTodayISO());
    const interval = setInterval(() => setEntryDate(getTodayISO()), 1000);
    return () => clearInterval(interval);
  }, [backdatedMode]);

  useEffect(() => {
    if (backdatedMode) return;
    setEntryShift(getCurrentShift());
    const interval = setInterval(() => setEntryShift(getCurrentShift()), 60000);
    return () => clearInterval(interval);
  }, [backdatedMode]);

  const exitBackdatedMode = () => {
    setBackdatedMode(false);
    setEntryDate(getTodayISO());
    setEntryShift(getCurrentShift());
  };

  useEffect(() => {
    memberInputRef.current = memberInput;
  }, [memberInput]);

  useEffect(() => {
    const fetchData = async () => {
      if (!entryShift || !entryDate) return;
      try {
        const dairy_id = localStorage.getItem("temp_key");
        const [todayData, response, customChartResponse] = await Promise.all([
          http.post('milk_data/get_today_data', { id: dairy_id, shift: entryShift, date: entryDate }),
          http.get('member/get_all_member', { params: { id: dairy_id } }),
          http.post('rate-chart/get')
        ]);
        if (todayData.code === "DATA_FOUND") {
          setTodaysData(() => todayData.data.records);
          let buffalo_stats = todayData.data.stats.find(k => k.type === "buffalo");
          let cow_stats = todayData.data.stats.find(k => k.type === "cow");
          setStats((prevStats) => ({
            ...prevStats,
            buffaloMilk: buffalo_stats ? buffalo_stats.total_liters : 0,
            buffaloRate: buffalo_stats ? buffalo_stats.total_amount : 0,
            cowMilk: cow_stats ? cow_stats.total_liters : 0,
            cowRate: cow_stats ? cow_stats.total_amount : 0,
            completedMembers: todayData.data.records.length,
          }))
        } else {
          // No entries at all for this date/shift — clear out whatever the
          // previous date/shift had, instead of leaving stale data on screen.
          setTodaysData([]);
          setStats((prevStats) => ({
            ...prevStats,
            buffaloMilk: 0,
            buffaloRate: 0,
            cowMilk: 0,
            cowRate: 0,
            completedMembers: 0,
          }));
        }
        if (response.code === "DATA_FOUND") {
          const member = response.data;
          setMembersData(member);
          resetForm();
          const matched = findMemberByDairyId(member, memberInputRef.current);
          setFormData(prev => { return { ...prev, milkType: matched ? matched.type : "" } });
          setSelectedMember(matched);
          setStats((prevStats) => ({
            ...prevStats,
            totalMembers: member.length,
          }));
        }

        // Load custom chart
        if (customChartResponse?.code === "DATA_FOUND") {
          setCustomChart(customChartResponse.data.chart || []);
        }
      } catch (error) {
        console.error("Error fetching:", error);
      }
    };
    fetchData();
  }, [entryDate, entryShift]);

  useEffect(() => {
    setShowTodaysData(todaysData.find((data) => data.dairy_id === selectedMember?.dairy_id) || null);
    setShowDetailsTab(false);
  }, [todaysData, selectedMember]);

  const calculate = (e) => {
    const { name, value } = e.target;
    let updatedData = { ...formData, [name]: value };
    const qty = (updatedData.qty) || 0;
    const fat = (updatedData.fat) || 0;
    const snf = (updatedData.snf) || 0;
    const type = updatedData.milkType.toLowerCase();
    if (qty && fat && snf && type) {
      const result = calculateMilkValues({ qty, fat, snf, type, customChart });
      if (result.error) {
        updatedData.rate = "0";
        updatedData.total_rate = "0";
        setRateError("अवैध फॅट किंवा एस एन एफ मूल्य!");
      } else {
        updatedData.rate = result.rate;
        updatedData.total_rate = result.t_rate;
        setRateError("");
      }
    } else {
      updatedData.rate = "0";
      updatedData.total_rate = "0";
      setRateError("");
    }
    setFormData(updatedData);
  };

  const handleMemberInput = (e) => {
    const value = e.target.value;
    setMemberInput(value);
    if (!value) return;
    const member = findMemberByDairyId(membersData, value);
    if (member) {
      resetForm();
      setSelectedMember(member);
      setFormData(prev => { return { ...prev, milkType: member.type } });
    } else {
      resetForm();
      setSelectedMember(null);
    }
  };

  const resetForm = () => {
    setFormData({
      qty: "",
      fat: "",
      snf: "",
      rate: "0",
      total_rate: "0",
      milkType: selectedMember?.type || "",
    });
    setRateError("");
    setEditingEntryId(null);
  };

  const handleEditEntry = () => {
    setFormData({
      qty: String(showTodaysData.qty),
      fat: String(showTodaysData.fat),
      snf: String(showTodaysData.snf),
      rate: showTodaysData.rate,
      total_rate: showTodaysData.total_rate,
      milkType: selectedMember?.type || "",
    });
    setRateError("");
    setEditingEntryId(showTodaysData.id);
  };

  const handlePrint = () => {
    const dairyName = localStorage.getItem("dairy_name") || "डेअरी";
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8" />
        <title>Receipt</title>
        <style>
          @page { margin: 0; size: 72mm auto; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Courier New', monospace; font-size: 11px; width: 72mm; padding: 4mm; }
          .header { text-align: center; border-bottom: 1px dashed #000; padding-bottom: 4px; margin-bottom: 4px; }
          .header h2 { font-size: 14px; font-weight: bold; }
          .header p { font-size: 11px; }
          .row { display: flex; justify-content: space-between; padding: 2px 0; font-size: 11px; }
          .totals { border-top: 1px dashed #000; margin-top: 6px; padding-top: 4px; }
          .totals .row { font-weight: bold; font-size: 13px; }
          .footer { text-align: center; border-top: 1px dashed #000; margin-top: 6px; padding-top: 4px; font-size: 10px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h2>${dairyName}</h2>
          <p>दूध नोंद पावती</p>
        </div>
        <div class="row"><span>दिनांक</span><span>${formatDisplayDate(entryDate)}</span></div>
        <div class="row"><span>सत्र</span><span>${entryShift === "morning" ? "सकाळ" : "संध्याकाळ"}</span></div>
        <div class="row"><span>सभासद क्र.</span><span>${selectedMember?.dairy_id ?? ""}</span></div>
        <div class="row"><span>नाव</span><span>${selectedMember?.mr_name ?? ""}</span></div>
        <div class="row"><span>लिटर</span><span>${showTodaysData.qty}</span></div>
        <div class="row"><span>फॅट</span><span>${showTodaysData.fat}</span></div>
        <div class="row"><span>एस एन एफ</span><span>${showTodaysData.snf}</span></div>
        <div class="totals">
          <div class="row"><span>दर/लिटर</span><span>₹${showTodaysData.rate}</span></div>
          <div class="row"><span>एकूण दर</span><span>₹${showTodaysData.total_rate}</span></div>
        </div>
        <div class="footer"><p>धन्यवाद!</p></div>
      </body>
      </html>`;

    // Print via a hidden same-page iframe rather than window.open(): mobile
    // browsers frequently block sized popup windows outright, and even when
    // they don't, a popup's onload is unreliable and iOS Safari often refuses
    // to print from a secondary window at all.
    let printFrame = document.getElementById("receipt-print-frame");
    if (!printFrame) {
      printFrame = document.createElement("iframe");
      printFrame.id = "receipt-print-frame";
      printFrame.style.position = "fixed";
      printFrame.style.right = "0";
      printFrame.style.bottom = "0";
      printFrame.style.width = "0";
      printFrame.style.height = "0";
      printFrame.style.border = "0";
      document.body.appendChild(printFrame);
    }

    const frameDoc = printFrame.contentWindow.document;
    frameDoc.open();
    frameDoc.write(printContent);
    frameDoc.close();

    printFrame.onload = () => {
      printFrame.contentWindow.focus();
      printFrame.contentWindow.print();
    };
  };

  const handleWhatsAppShare = () => {
    const message = 
`🐄 दूध नोंद - ${formatDisplayDate(entryDate)} (${entryShift === "morning" ? "सकाळ" : "संध्याकाळ"})
सभासद: ${selectedMember?.mr_name} (${selectedMember?.dairy_id})
लिटर: ${showTodaysData.qty}
फॅट: ${showTodaysData.fat}
एस एन एफ: ${showTodaysData.snf}
दर/लिटर: ₹${showTodaysData.rate}
एकूण: ₹${showTodaysData.total_rate}`;
    const memberPhone = selectedMember?.phone?.replace(/\D/g, "");
    const target = memberPhone ? `91${memberPhone}` : "";
    window.open(`https://wa.me/${target}?text=${encodeURIComponent(message)}`, "_blank");
  };

  const [pendingUpdate, setPendingUpdate] = useState(null);

  // New entries submit straight away; editing an existing entry shows a
  // before/after confirm popup first (there's nothing to diff for a new one).
  const requestSubmit = () => {
    if (!formData.qty || !formData.fat || !formData.snf) {
      ToastService.warning("कृपया सर्व फील्ड भरा!");
      return;
    }
    if (rateError) {
      ToastService.warning("कृपया योग्य फॅट व एस एन एफ मूल्य प्रविष्ट करा!");
      return;
    }
    if (editingEntryId) {
      setPendingUpdate({ before: showTodaysData, after: { ...formData } });
    } else {
      doSubmit();
    }
  };

  const cancelConfirm = () => setPendingUpdate(null);

  const doSubmit = async () => {
    const dairy_id = localStorage.getItem("temp_key");
    const isEditing = !!editingEntryId;
    let endpoint = 'milk_data/milk_entry';
    let body;
    if (isEditing) {
      endpoint = 'milk_data/update_milk_entry';
      body = {
        id: editingEntryId,
        mst_id: dairy_id,
        qty: formData.qty,
        fat: formData.fat,
        snf: formData.snf,
        rate: formData.rate,
        total_rate: formData.total_rate,
        en_milking_shift: entryShift,
      };
    } else {
      body = {
        ...formData,
        mst_id: dairy_id,
        mem_id: selectedMember.id,
        en_milking_shift: entryShift,
        mr_milking_shift: entryShift === "morning" ? "सकाळ" : "संध्याकाळ",
        date: entryDate,
        entry_time: new Date().toLocaleTimeString('en-IN', { hour12: false }),
      };
      delete body.milkType;
    }
    try {
      let response = await http.post(endpoint, body);
      if (response?.code === "ENTRY_ALREADY_EXISTS") {
        ToastService.warning("या वेळेसाठी नोंद अस्तित्वात आहे!");
        return;
      }
      if (response?.code === "ENTRY_ALREADY_BILLED") {
        ToastService.error(
          isEditing
            ? "या नोंदीचे बिल आधीच तयार झाले आहे, संपादन करता येणार नाही."
            : "या तारखेचे बिल आधीच तयार झाले आहे, नवीन नोंद जोडता येणार नाही."
        );
        setPendingUpdate(null);
        return;
      }
      if (!response || !response.data) {
        ToastService.error(isEditing ? "अपडेट अयशस्वी ❌" : "नोंद अयशस्वी ❌");
        return;
      }
      setTodaysData(() => response.data.records);
      let buffalo_stats = response.data.stats.find(k => k.type === "buffalo");
      let cow_stats = response.data.stats.find(k => k.type === "cow");
      setStats((prevStats) => ({
        ...prevStats,
        buffaloMilk: buffalo_stats ? buffalo_stats.total_liters : 0,
        buffaloRate: buffalo_stats ? buffalo_stats.total_amount : 0,
        cowMilk: cow_stats ? cow_stats.total_liters : 0,
        cowRate: cow_stats ? cow_stats.total_amount : 0,
        completedMembers: response.data.records.length,
      }));
      setEditingEntryId(null);
      setPendingUpdate(null);
      if (isEditing) {
        ToastService.success("नोंद अपडेट झाली ✅");
      }
    } catch (error) {
      console.error("Error submitting milk entry:", error);
      ToastService.error(apiMessage());
    }
  };

  const isSubmitDisabled = !formData.qty || !formData.fat || !formData.snf || !!rateError;

  const buffaloMilk = Number(Stats.buffaloMilk) || 0;
  const buffaloRate = Number(Stats.buffaloRate) || 0;
  const cowMilk = Number(Stats.cowMilk) || 0;
  const cowRate = Number(Stats.cowRate) || 0;

  return (
    <div className="dashboard-container">
      {/* Date and Session Info */}
      <div className="nav2">
        <h3>{formatDisplayDate(entryDate)}{backdatedMode && <span className="backdate-badge"> (मागील)</span>}</h3>
        <h3>{entryShift === "morning" ? "सकाळ" : "संध्याकाळ"}</h3>
        <h3>सभासद {Stats.completedMembers}/{Stats.totalMembers}</h3>
      </div>

      <div className="main">
        {!showDetailsTab && backdatedMode && (
          <div className="backdate-toggle-row">
            <div className="backdate-panel">
              <input
                type="date"
                className="form-input"
                value={entryDate}
                max={getTodayISO()}
                onChange={(e) => e.target.value && setEntryDate(e.target.value)}
              />
              <div className="backdate-shift-btns">
                <button
                  type="button"
                  className={`backdate-shift-btn ${entryShift === "morning" ? "active" : ""}`}
                  onClick={() => setEntryShift("morning")}
                >
                  सकाळ
                </button>
                <button
                  type="button"
                  className={`backdate-shift-btn ${entryShift === "evening" ? "active" : ""}`}
                  onClick={() => setEntryShift("evening")}
                >
                  संध्याकाळ
                </button>
              </div>
              <button type="button" className="backdate-close-btn" onClick={exitBackdatedMode}>
                आजची नोंद
              </button>
            </div>
          </div>
        )}

        {/* Member Info */}
        <div className="stats-container-2">
          <div className="input-container">
            <input
              type="number"
              className="form-input stat-input"
              value={memberInput}
              onChange={handleMemberInput}
              onKeyDown={(e) => onlyNumber(e, false)}
              min={1}
            />
          </div>
          <div className="additional-info">
            {selectedMember ? selectedMember.mr_name : ""}
          </div>
          {selectedMember ? (
            <Button
              variant="contained"
              onClick={() => setShowDetailsTab((prev) => !prev)}
              className="info-btn"
            >
              {showDetailsTab ? "नोंद" : "माहिती"}
            </Button>
          ) : <></>}
        </div>

        <div className="action-section">
          <button className={`submit-btn btn-${selectedMember ? formData.milkType : "danger"}`}>
            <strong>{!selectedMember ? "सभासद अस्तित्वात नाही!" : formData.milkType === "buffalo" ? "म्हैस" : "गाय"}</strong>
          </button>
        </div>

        {selectedMember ? (showDetailsTab ? (
          <DetailsTab mem_id={selectedMember.id} phone={selectedMember.phone} />
        ) : (
          <>
            {(!showTodaysData || editingEntryId) ? (
              <>
                <div className="form-section">
                  <div className="form-row">
                    <label className="form-label label-xl">दूध (लिटर) :</label>
                    <input
                      type="number"
                      name="qty"
                      min={1}
                      value={formData.qty}
                      onChange={calculate}
                      onKeyDown={onlyNumber}
                      className="form-input"
                    />
                  </div>

                  <div className="form-row">
                    <label className="form-label label-xl">फॅट:</label>
                    <input
                      type="number"
                      name="fat"
                      min={1}
                      value={formData.fat}
                      onChange={calculate}
                      onKeyDown={onlyNumber}
                      className="form-input"
                    />
                  </div>

                  <div className="form-row">
                    <label className="form-label label-xl">एस एन एफ:</label>
                    <input
                      min={1}
                      type="number"
                      name="snf"
                      value={formData.snf}
                      onChange={calculate}
                      onKeyDown={onlyNumber}
                      className="form-input"
                    />
                  </div>
                </div>

                {rateError ? (
                  <label className="error-label">{rateError}</label>
                ) : null}

                {/* Bottom Stats */}
                <div className="bottom-stats">
                  <div className="bottom-stat">
                    <div className="bottom-label label-xl">दर/लिटर</div>
                    <div className="bottom-value">{formData.rate || "0"}</div>
                  </div>

                  <div className="bottom-stat">
                    <div className="bottom-label label-xl">एकूण दर</div>
                    <div className="bottom-value">
                      {formData.total_rate || "0"}
                    </div>
                  </div>
                </div>

                <div className="action-section">
                  <button
                    className={`submitbtn ${isSubmitDisabled ? "" : "active"}`}
                    onClick={requestSubmit}
                    disabled={isSubmitDisabled}
                  >
                    {editingEntryId ? "अपडेट करा" : "OKAY"}
                  </button>
                  {editingEntryId ? (
                    <>
                      {" "}
                      <button className="submitbtn secondary" onClick={resetForm}>
                        रद्द करा
                      </button>
                    </>
                  ) : null}
                </div>
              </>
            ) : (
              <>
                {/* Summary View */}
                <div className="summary-section">
                  <div className="summary-row">
                    <div>लिटर:</div> <div className="summary-value">{showTodaysData.qty}</div>
                  </div>
                  <div className="summary-row">
                    <div>फॅट:</div> <div className="summary-value">{showTodaysData.fat}</div>
                  </div>
                  <div className="summary-row">
                    <div>एस एन एफ:</div> <div className="summary-value">{showTodaysData.snf}</div>
                  </div>
                  <div className="summary-row">
                    <div>दर/लिटर(रु.):</div> <div className="summary-value">{showTodaysData.rate} ₹</div>
                  </div>
                  <div className="summary-row">
                    <div>एकूण दर(रु.):</div> <div className="summary-value">{showTodaysData.total_rate} ₹</div>
                  </div>
                </div>

                {/* Print / WhatsApp / Edit Buttons */}
                <div className="action-section">
                  <button className="submitbtn active icon-btn" onClick={handlePrint} title="Print" aria-label="Print">
                    <PrintIcon />
                  </button>
                  <button className="submitbtn whatsapp icon-btn" onClick={handleWhatsAppShare} title="WhatsApp" aria-label="WhatsApp">
                    <WhatsAppIcon />
                  </button>
                  <button className="submitbtn secondary icon-btn" onClick={handleEditEntry} title="सुधारणा" aria-label="सुधारणा">
                    <EditIcon />
                  </button>
                </div>
              </>
            )}
          </>
        )) : <></>}

        {/* Cow / Buffalo / Total summary — milk-entry view only, hidden on
            Bill/Advance/Supply (shown via DetailsTab) */}
        {!showDetailsTab && (
          <div className="dr-table-wrap dr-summary-wrap">
            <table className="dr-table dr-summary-table">
              <thead>
                <tr>
                  <th></th>
                  <th>म्हैस</th>
                  <th>गाय</th>
                  <th>एकूण</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="dr-summary-label">दूध (लि.)</td>
                  <td>{buffaloMilk.toFixed(2)}</td>
                  <td>{cowMilk.toFixed(2)}</td>
                  <td className="dr-amount">{(buffaloMilk + cowMilk).toFixed(2)}</td>
                </tr>
                <tr>
                  <td className="dr-summary-label">रक्कम (₹)</td>
                  <td>{buffaloRate.toFixed(2)}</td>
                  <td>{cowRate.toFixed(2)}</td>
                  <td className="dr-amount">{(buffaloRate + cowRate).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {!showDetailsTab && !backdatedMode && (
          <div className="backdate-toggle-row backdate-toggle-row--bottom">
            <button type="button" className="backdate-open-btn" onClick={() => setBackdatedMode(true)}>
              + पूर्वीची नोंद जोडा
            </button>
          </div>
        )}
      </div>

      {pendingUpdate && (
        <div className="bd-confirm-overlay" onClick={cancelConfirm}>
          <div className="bd-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>बदल जतन करायचे आहेत का?</h3>
            <table className="bd-confirm-table">
              <thead>
                <tr><th></th><th>आधी</th><th>नंतर</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td>लिटर</td>
                  <td>{pendingUpdate.before.qty}</td>
                  <td className={String(pendingUpdate.before.qty) !== String(pendingUpdate.after.qty) ? "bd-changed" : ""}>{pendingUpdate.after.qty}</td>
                </tr>
                <tr>
                  <td>फॅट</td>
                  <td>{pendingUpdate.before.fat}</td>
                  <td className={String(pendingUpdate.before.fat) !== String(pendingUpdate.after.fat) ? "bd-changed" : ""}>{pendingUpdate.after.fat}</td>
                </tr>
                <tr>
                  <td>SNF</td>
                  <td>{pendingUpdate.before.snf}</td>
                  <td className={String(pendingUpdate.before.snf) !== String(pendingUpdate.after.snf) ? "bd-changed" : ""}>{pendingUpdate.after.snf}</td>
                </tr>
                <tr>
                  <td>दर/लि.</td>
                  <td>₹{pendingUpdate.before.rate}</td>
                  <td className={String(pendingUpdate.before.rate) !== String(pendingUpdate.after.rate) ? "bd-changed" : ""}>₹{pendingUpdate.after.rate}</td>
                </tr>
                <tr>
                  <td>एकूण रक्कम</td>
                  <td>₹{pendingUpdate.before.total_rate}</td>
                  <td className={String(pendingUpdate.before.total_rate) !== String(pendingUpdate.after.total_rate) ? "bd-changed" : ""}>₹{pendingUpdate.after.total_rate}</td>
                </tr>
              </tbody>
            </table>
            <div className="bd-confirm-actions">
              <button className="submitbtn active" onClick={doSubmit}>पुष्टी करा</button>
              <button className="submitbtn secondary" onClick={cancelConfirm}>रद्द करा</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashBoard;